import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { useGetPayeeList } from '@/features/ap_payees/components/DocInstructionMenu/Hooks/useGetPayeeList';
import {
  InstructionPdfDialogContentsProps,
  InstructionPdfForm,
} from '@/features/ap_payees/components/DocInstructionMenu/types';
import { SearchApPayeeQuery, SearchedApPayee } from 'ap-openapi';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ListPayee } from '../../ListPayee';
import { useDownloadInstructions } from './Hooks/useDownloadInstructions';
import { InstructionFooter } from './InstructionFooter';
import { InstructionInformation } from './InstructionInformation';
import { PreviewInstruction } from './PreviewInstruction';
import styles from './index.module.scss';

const InnerContent = ({
  onClose,
  payeeQuery,
  payeeList,
  isFormValid,
  setError,
}: {
  onClose: () => void;
  payeeQuery?: SearchApPayeeQuery;
  payeeList: SearchedApPayee[];
  isFormValid: boolean;
  setError: (error: Error) => void;
}) => {
  const { handleDownload, isLoading } = useDownloadInstructions(
    payeeQuery,
    payeeList,
    setError
  );

  return (
    <InstructionFooter
      onCancel={onClose}
      onDownload={handleDownload}
      isDownloading={isLoading}
      isFormValid={isFormValid}
    />
  );
};

export const Contents: FC<InstructionPdfDialogContentsProps> = ({
  onClose,
  payees,
  payeeQuery,
}) => {
  const [{ setError }, ErrorContextHolder, ErrorInlineNotificationProvider] =
    useErrorInlineNotification();

  const {
    payeeList,
    isFetchingNextPage,
    loadMorePayeeList,
    hasPreviousPayee,
    hasNextPayee,
    onPreviewPreviousPayee,
    onPreviewNextPayee,
    totalPayees,
    currentPreviewIndex,
    currentPreviewPayee,
  } = useGetPayeeList(payees, payeeQuery);

  const methods = useForm<InstructionPdfForm>({
    defaultValues: {
      destination: '',
      sender: '',
      title: '',
      content: '',
      saveAsTemplate: false,
    },
    mode: 'all',
  });

  const { formState } = methods;
  const isFormValid = formState.isValid;

  return (
    <ErrorInlineNotificationProvider value={setError}>
      <FormProvider {...methods}>
        <div className={styles.container}>
          {ErrorContextHolder.props.visible && (
            <div className={styles.notification}>{ErrorContextHolder}</div>
          )}
          <ListPayee
            payees={payeeList}
            isFetchingNextPage={isFetchingNextPage}
            loadMorePayeeList={loadMorePayeeList}
            totalPayees={totalPayees}
          />
          <div className={styles.grid_container}>
            <div>
              <InstructionInformation />
            </div>
            <div>
              <PreviewInstruction
                currentPayee={currentPreviewIndex + 1}
                totalPayees={totalPayees}
                onPreviewPreviousPayee={onPreviewPreviousPayee}
                onPreviewNextPayee={onPreviewNextPayee}
                hasPreviousPayee={hasPreviousPayee}
                hasNextPayee={hasNextPayee}
                currentPreviewPayee={currentPreviewPayee}
                setError={setError}
              />
            </div>
          </div>
        </div>
        <InnerContent
          onClose={onClose}
          payeeQuery={payeeQuery}
          payeeList={payeeList}
          isFormValid={isFormValid}
          setError={setError}
        />
      </FormProvider>
    </ErrorInlineNotificationProvider>
  );
};
